import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type NomenclatureSeries, type NomenclatureCharacteristic } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';

/**
 * Закладка «Серії/Характеристики» картки номенклатури (підфіча #4).
 *
 * Дві секції в одній закладці:
 * - Серії (партії) — code / name / expiry_date / manufacture_date.
 * - Характеристики (варіанти) — code / name.
 *
 * Серії/характеристики — каскад через номенклатуру (картка tenant-scoped),
 * тому org-вибір НЕ потрібен (на відміну від «Комплектації»).
 */

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Помилка збереження';
  }
  return String(e);
}

// ── Локальний стан рядка-форми (для нового / редагованого).
interface SeriesDraft {
  name: string;
  code: string;
  expiry_date: string;
  manufacture_date: string;
}
interface CharDraft {
  name: string;
  code: string;
}

const EMPTY_SERIES: SeriesDraft = { name: '', code: '', expiry_date: '', manufacture_date: '' };
const EMPTY_CHAR: CharDraft = { name: '', code: '' };

export function NomenclatureSeriesTab({ nomenclatureId }: { nomenclatureId: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <SeriesSection nomenclatureId={nomenclatureId} />
      <CharacteristicsSection nomenclatureId={nomenclatureId} />
    </div>
  );
}

// ─────────────────────────── Серії (партії) ───────────────────────────
function SeriesSection({ nomenclatureId }: { nomenclatureId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const queryKey = ['nomenclature-series', nomenclatureId];
  const list = useQuery({
    queryKey,
    queryFn: () => refs.nomenclatureSeries(nomenclatureId),
  });

  // editing: null = нічого, 'new' = новий рядок, інакше id існуючого.
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<SeriesDraft>(EMPTY_SERIES);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: draft.name.trim(),
        code: draft.code.trim() || null,
        expiry_date: draft.expiry_date || null,
        manufacture_date: draft.manufacture_date || null,
      };
      if (editing === 'new') {
        return refs.createNomenclatureSeries({ nomenclature_id: nomenclatureId, ...payload });
      }
      return refs.updateNomenclatureSeries(editing as string, payload);
    },
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e) => setError(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => refs.deleteNomenclatureSeries(id),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });

  const startCreate = () => { setDraft(EMPTY_SERIES); setEditing('new'); setError(null); };
  const startEdit = (s: NomenclatureSeries) => {
    setDraft({
      name: s.name,
      code: s.code ?? '',
      expiry_date: s.expiry_date ?? '',
      manufacture_date: s.manufacture_date ?? '',
    });
    setEditing(s.id);
    setError(null);
  };
  const cancel = () => { setEditing(null); setError(null); };

  const draftCells = (
    <>
      <td style={{ padding: '0.3rem' }}>
        <input value={draft.code} placeholder="код"
          onChange={(e) => setDraft({ ...draft, code: e.target.value })}
          style={{ width: 90 }} />
      </td>
      <td style={{ padding: '0.3rem' }}>
        <input value={draft.name} placeholder="назва серії"
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          style={{ width: '100%' }} />
      </td>
      <td style={{ padding: '0.3rem' }}>
        <DatePopover value={draft.manufacture_date}
          onChange={(v) => setDraft({ ...draft, manufacture_date: v })} />
      </td>
      <td style={{ padding: '0.3rem' }}>
        <DatePopover value={draft.expiry_date}
          onChange={(v) => setDraft({ ...draft, expiry_date: v })} />
      </td>
      <td style={{ padding: '0.3rem', whiteSpace: 'nowrap' }}>
        <button className="primary" disabled={!draft.name.trim() || save.isPending}
          onClick={() => save.mutate()}>✓</button>{' '}
        <button onClick={cancel}>×</button>
      </td>
    </>
  );

  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('uk-UA') : '—');

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.35rem' }}>
        <h3 style={{ margin: 0 }}>Серії (партії)</h3>
        {editing === null && (
          <button style={{ marginLeft: 'auto' }} onClick={startCreate}>+ Додати серію</button>
        )}
      </div>
      <p style={{ color: 'var(--muted)', margin: '0 0 0.5rem', fontSize: '0.85em' }}>
        Серія (партія) — для обліку строків придатності та дат виготовлення.
      </p>
      {error && editing !== null && <div className="error" style={{ marginBottom: '0.5rem' }}>{error}</div>}
      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 110, textAlign: 'left' }}>Код</th>
            <th style={{ textAlign: 'left' }}>Назва</th>
            <th style={{ width: 140, textAlign: 'left' }}>Дата виготовлення</th>
            <th style={{ width: 140, textAlign: 'left' }}>Придатна до</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {list.isLoading && (
            <tr><td colSpan={5} style={{ color: 'var(--muted)' }}>Завантаження…</td></tr>
          )}
          {(list.data ?? []).map((s) =>
            editing === s.id ? (
              <tr key={s.id} style={{ background: '#fefce8' }}>{draftCells}</tr>
            ) : (
              <tr key={s.id}>
                <td>{s.code ?? '—'}</td>
                <td>{s.name}</td>
                <td>{fmtDate(s.manufacture_date)}</td>
                <td>{fmtDate(s.expiry_date)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button title="Редагувати" disabled={editing !== null} onClick={() => startEdit(s)}>✎</button>{' '}
                  <button title="Видалити" className="danger" disabled={editing !== null}
                    onClick={() => confirm('Видалити серію?').then((ok) => { if (ok) remove.mutate(s.id); })}>✕</button>
                </td>
              </tr>
            ),
          )}
          {editing === 'new' && <tr style={{ background: '#fefce8' }}>{draftCells}</tr>}
          {!list.isLoading && (list.data?.length ?? 0) === 0 && editing !== 'new' && (
            <tr><td colSpan={5} style={{ color: 'var(--muted)' }}>Серій немає.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

// ─────────────────────── Характеристики (варіанти) ───────────────────────
function CharacteristicsSection({ nomenclatureId }: { nomenclatureId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const queryKey = ['nomenclature-characteristics', nomenclatureId];
  const list = useQuery({
    queryKey,
    queryFn: () => refs.nomenclatureCharacteristics(nomenclatureId),
  });

  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<CharDraft>(EMPTY_CHAR);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: draft.name.trim(), code: draft.code.trim() || null };
      if (editing === 'new') {
        return refs.createNomenclatureCharacteristic({ nomenclature_id: nomenclatureId, ...payload });
      }
      return refs.updateNomenclatureCharacteristic(editing as string, payload);
    },
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e) => setError(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => refs.deleteNomenclatureCharacteristic(id),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });

  const startCreate = () => { setDraft(EMPTY_CHAR); setEditing('new'); setError(null); };
  const startEdit = (c: NomenclatureCharacteristic) => {
    setDraft({ name: c.name, code: c.code ?? '' });
    setEditing(c.id);
    setError(null);
  };
  const cancel = () => { setEditing(null); setError(null); };

  const draftCells = (
    <>
      <td style={{ padding: '0.3rem' }}>
        <input value={draft.code} placeholder="код"
          onChange={(e) => setDraft({ ...draft, code: e.target.value })}
          style={{ width: 90 }} />
      </td>
      <td style={{ padding: '0.3rem' }}>
        <input value={draft.name} placeholder="назва характеристики"
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          style={{ width: '100%' }} />
      </td>
      <td style={{ padding: '0.3rem', whiteSpace: 'nowrap' }}>
        <button className="primary" disabled={!draft.name.trim() || save.isPending}
          onClick={() => save.mutate()}>✓</button>{' '}
        <button onClick={cancel}>×</button>
      </td>
    </>
  );

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.35rem' }}>
        <h3 style={{ margin: 0 }}>Характеристики (варіанти)</h3>
        {editing === null && (
          <button style={{ marginLeft: 'auto' }} onClick={startCreate}>+ Додати характеристику</button>
        )}
      </div>
      <p style={{ color: 'var(--muted)', margin: '0 0 0.5rem', fontSize: '0.85em' }}>
        Характеристика (варіант) — колір, розмір тощо для обліку варіантів товару.
      </p>
      {error && editing !== null && <div className="error" style={{ marginBottom: '0.5rem' }}>{error}</div>}
      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 110, textAlign: 'left' }}>Код</th>
            <th style={{ textAlign: 'left' }}>Назва</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {list.isLoading && (
            <tr><td colSpan={3} style={{ color: 'var(--muted)' }}>Завантаження…</td></tr>
          )}
          {(list.data ?? []).map((c) =>
            editing === c.id ? (
              <tr key={c.id} style={{ background: '#fefce8' }}>{draftCells}</tr>
            ) : (
              <tr key={c.id}>
                <td>{c.code ?? '—'}</td>
                <td>{c.name}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button title="Редагувати" disabled={editing !== null} onClick={() => startEdit(c)}>✎</button>{' '}
                  <button title="Видалити" className="danger" disabled={editing !== null}
                    onClick={() => confirm('Видалити характеристику?').then((ok) => { if (ok) remove.mutate(c.id); })}>✕</button>
                </td>
              </tr>
            ),
          )}
          {editing === 'new' && <tr style={{ background: '#fefce8' }}>{draftCells}</tr>}
          {!list.isLoading && (list.data?.length ?? 0) === 0 && editing !== 'new' && (
            <tr><td colSpan={3} style={{ color: 'var(--muted)' }}>Характеристик немає.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
